import { ReactElement, useContext, useState, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from '@mui/icons-material';

import { PopupType } from '@/types';
import { popupDefs } from '@/popups';
import { ctxPopup, PopupContext } from '@/contexts/PopupContext';
import { ctxUser } from '@/contexts/UserContext';

import './Home.css';

export function HomeLeftNav({ logout }: { logout: () => void }) {
  const popupCtx = useContext(ctxPopup);
  const userCtx = useContext(ctxUser);
  const ref = useRef<HTMLAnchorElement | null>(null);
  const { t } = useTranslation();
  const client = useQueryClient();
  const [open, setOpen] = useState<boolean>(() => {
    const savedState = localStorage.getItem('drawerState');
    return savedState ? JSON.parse(savedState) : false;
  });
  const [selected, setSelected] = useState(-1);
  const [secondaryMenu, setSecondaryMenu] = useState<ReactElement | undefined>(undefined);

  useEffect(() => {
    return () => popupCtx?.setPopupMain(undefined);
  }, []);

  useEffect(() => {
    localStorage.setItem('drawerState', JSON.stringify(open));
  }, [open]);

  const popups: PopupType[] = useMemo(() => {
    let popups = popupDefs({
      close: () => {
        popupCtx?.setPopupMain(undefined);
        setSelected(-1);
      },
      logout,
      ref,
      client,
      isBottom: false,
    });
    if (userCtx?.isToken()) {
      popups = popups.filter((p) => p.showToken);
    }
    return popups;
  }, [logout, popupCtx, userCtx, client]);

  if (!popupCtx) return;

  const mainPopups = popups.filter((p) => p.mainFunction && hasExtraFeature(p));
  const secondaryPopups = popups.filter((p) => !p.mainFunction && hasExtraFeature(p));

  function hasExtraFeature(popup: PopupType) {
    const requiredExtraFeature = popup.requiredExtraFeature;
    if (!requiredExtraFeature) {
      return true;
    }
    return userCtx?.hasExtraFeature(requiredExtraFeature);
  }

  function updateMainMenu(i: number, offset: number, element: ReactElement | undefined) {
    if (selected === i + offset) {
      setSecondaryMenu(undefined);
      setSelected(-1);
    } else {
      setSecondaryMenu(element);
      setSelected(i + offset);
    }
  }

  function updateSecondaryMenu(popupCtx: PopupContext, i: number, offset: number, element: ReactElement | undefined) {
    if (selected === i + offset) {
      popupCtx.setPopupMain(undefined);
      setSelected(-1);
    } else {
      popupCtx.setPopupMain(element);
      setSelected(i + offset);
    }
  }
  // todo: ami még hiányzik: telefonos nézetre váltásból ne maradjon ott ami ottmarad
  function createIconList(popups: PopupType[], popupCtx: PopupContext, offset: number) {
    return (
      <>
        <List>
          {popups.map((p, i) => (
            <ListItem key={p.title}>
              <ListItemButton
                className={`home-left-nav-list-button${selected == i + offset ? ' Mui-selected' : ''}`}
                onClick={
                  p.element !== undefined
                    ? () => {
                        if (userCtx?.isToken()) {
                          if (i + offset === 0) {
                            updateMainMenu(i, offset, p.element);
                          } else {
                            updateSecondaryMenu(popupCtx, i, offset, p.element);
                          }
                        } else {
                          if (i + offset < mainPopups.length) {
                            updateMainMenu(i, offset, p.element);
                          } else {
                            updateSecondaryMenu(popupCtx, i, offset, p.element);
                          }
                        }
                      }
                    : () => logout()
                }
                data-qa-element-id={`left-navigation-action-${p.title}`}
              >
                <ListItemIcon className={open ? '' : 'home-left-nav-open-list'}>{p.icon}</ListItemIcon>
                {open && <ListItemText className="home-left-nav-listitem-text" primary={t(p.title)} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </>
    );
  }

  return (
    <>
      <Box className={`home-nav ${open ? 'home-left-nav-opened' : 'home-left-nav-closed'}`}>
        <Drawer
          variant="permanent"
          className={`home-left-nav-navigator ${open ? 'home-left-nav-opened' : 'home-left-nav-closed'}`}
          classes={{ paper: open ? 'home-left-nav-opened' : 'home-left-nav-closed' }}
        >
          <div className="home-left-nav-list">
            {createIconList(mainPopups, popupCtx, 0)}
            <div className="home-left-nav-secondary-list">
              {createIconList(secondaryPopups, popupCtx, mainPopups.length)}
            </div>
          </div>
          <div className="home-left-nav-list-close-container">
            <Button
              onClick={() => setOpen(!open)}
              className="home-left-nav-list-close"
              data-qa-element-id="home-left-nav-expand-collapse"
            >
              {open ? <KeyboardDoubleArrowLeft fontSize="medium" /> : <KeyboardDoubleArrowRight fontSize="medium" />}
            </Button>
          </div>
        </Drawer>
      </Box>
      {secondaryMenu}
    </>
  );
}
